import React, { useState, useEffect } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import MoreIcon from "../../svg/MoreIcon";
import RulesPreview from "./RulesPreview";
import { enReportServiceType, enTimeUnit } from "../seviceBuilder/helper";
import MoreOptionsPopover, { MenuOption } from "../common/MoreOptionsPopover";
import {
  deleteUserLogicById,
  updateLogicStatus,
  updateLogic,
} from "../../../../common/network/Api";
import { useAppStore } from "../../../../providers/AppStore";
import IOSSwitch from "../common/IOSSwitch";
import ScrollableWrapper from "../common/ScrollableWrapper";
import PageLoader from "../../../../common/ui/PageLoader";
import EditServiceModal from "./EditServiceModal";
import EditLabPanelModal from "./EditLabPanelModal";
import NewLogicModal from "./NewLogicModal";
import { renderAttributeValue } from "../common/attributeUtils";
import PanelSummaryCard from "../common/PanelSummaryCard";
import { displayLabPanelName } from "../../utils/labPanelName";

interface LogicDetailsViewProps {
  logic: any;
  /**
   * Return to the listing. Not a header affordance — the breadcrumb carries
   * the back link (`useBackNavigation`) — but a deleted logic's detail must
   * not stay open, so the delete path calls this.
   */
  onBack: () => void;
  onEdit: (logic: any) => void;
  onUpdate?: () => void;
  onDelete?: () => void;
}

const LogicDetailsView = ({
  logic,
  onBack,
  onEdit,
  onUpdate,
  onDelete,
}: LogicDetailsViewProps) => {
  const theme: Theme = useTheme();
  const [isEnabled, setIsEnabled] = useState(logic.enabled === 1);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editServiceModalOpen, setEditServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<any | null>(null);
  const [editLabPanelModalOpen, setEditLabPanelModalOpen] = useState(false);
  const [selectedLabPanel, setSelectedLabPanel] = useState<any | null>(null);
  const [editLogicModalOpen, setEditLogicModalOpen] = useState(false);
  const {
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
    ASSetSnackBarActions,
  } = useAppStore();

  // Update isEnabled when logic.enabled changes (e.g., after refetch)
  useEffect(() => {
    setIsEnabled(logic.enabled === 1);
  }, [logic.enabled]);

  const handleToggleEnable = async () => {
    const newStatus = !isEnabled;
    const statusValue = newStatus ? 1 : 0;

    setIsTogglingStatus(true);
    try {
      await updateLogicStatus(logic.id, statusValue);
      setIsEnabled(newStatus);

      // Refetch logics to update the list (without navigation)
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Failed to update logic status:", error);
      ASSetSnackBarMessage({
        title: "Error",
        message: "Failed to update logic status. Please try again.",
      });
      ASSetTypeSnackBar("alert");
      ASSetOpenSnackBar(true);
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const handleEdit = () => {
    setEditLogicModalOpen(true);
  };

  const handleLogicSaved = async () => {
    // Refresh the logic data to get the latest changes
    if (onUpdate) {
      await onUpdate();
    }
    setEditLogicModalOpen(false);
  };

  const handleDelete = () => {
    // Show warning confirmation before deleting
    ASSetSnackBarMessage({
      title: "Delete logic",
      message:
        "Are you sure you want to delete this logic and its included services? This action cannot be undone.",
    });
    ASSetTypeSnackBar("warning");
    ASSetSnackBarActions({
      onConfirm: async () => {
        if (!logic.id) {
          console.error("Logic ID is missing");
          return;
        }

        setIsDeleting(true);
        try {
          await deleteUserLogicById(logic.id);

          // Show success message
          ASSetOpenSnackBar(true);
          ASSetSnackBarMessage({
            title: "Success",
            message: `Logic "${logic.name}" deleted successfully!`,
          });
          ASSetTypeSnackBar("success");

          // Call onDelete callback if provided, or just go back
          if (onDelete) {
            onDelete();
          }
          onBack();
        } catch (error) {
          console.error("Error deleting logic:", error);

          // Show error message
          ASSetOpenSnackBar(true);
          ASSetSnackBarMessage({
            title: "Error",
            message: "Failed to delete logic. Please try again.",
          });
          ASSetTypeSnackBar("alert");
        } finally {
          setIsDeleting(false);
        }
      },
      onCancel: () => {
        // Just close the snackbar
      },
    });
    ASSetOpenSnackBar(true);
  };

  const menuOptions: MenuOption[] = [
    { label: "Edit logic", onClick: handleEdit },
    { label: "Delete logic", onClick: handleDelete },
  ];

  // Service-specific menu handlers
  const handleEditService = (service: any) => {
    setSelectedService(service);
    setEditServiceModalOpen(true);
  };

  const handleSaveService = async (updatedService: any) => {
    try {
      // Update the service in the services array
      const updatedServices = logic.services.map((s: any) =>
        s.id === updatedService.id ? updatedService : s
      );

      // Prepare the updated logic data
      const updatedLogicData = {
        ...logic,
        services: updatedServices,
      };

      // Call the update API
      await updateLogic(updatedLogicData);

      // Show success message
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: "Success",
        message: `Service "${updatedService.name}" updated successfully!`,
      });
      ASSetTypeSnackBar("success");

      // Refresh the logic data
      if (onUpdate) {
        await onUpdate();
      }

      // Close the modal
      setEditServiceModalOpen(false);
      setSelectedService(null);
    } catch (error) {
      console.error("Error updating service:", error);

      // Show error message
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: "Error",
        message: "Failed to update service. Please try again.",
      });
      ASSetTypeSnackBar("alert");
    }
  };

  const handleDeleteService = (service: any) => {
    // Show warning confirmation before deleting
    ASSetSnackBarMessage({
      title: "Delete service",
      message:
        "Are you sure you want to delete this service from the logic? This action cannot be undone.",
    });
    ASSetTypeSnackBar("warning");
    ASSetSnackBarActions({
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          // Remove the service from the services array
          const updatedServices = logic.services.filter(
            (s: any) => s.id !== service.id
          );

          // Prepare the updated logic data
          const updatedLogicData = {
            ...logic,
            services: updatedServices,
          };

          // Call the update API
          await updateLogic(updatedLogicData);

          // Show success message
          ASSetOpenSnackBar(true);
          ASSetSnackBarMessage({
            title: "Success",
            message: `Service "${service.name}" deleted successfully!`,
          });
          ASSetTypeSnackBar("success");

          // Refresh the logic data
          if (onUpdate) {
            await onUpdate();
          }
        } catch (error) {
          console.error("Error deleting service:", error);

          // Show error message
          ASSetOpenSnackBar(true);
          ASSetSnackBarMessage({
            title: "Error",
            message: "Failed to delete service. Please try again.",
          });
          ASSetTypeSnackBar("alert");
        } finally {
          setIsDeleting(false);
        }
      },
      onCancel: () => {
        // Just close the snackbar
      },
    });
    ASSetOpenSnackBar(true);
  };

  // Lab panel handlers
  const handleEditLabPanel = (labPanel: any) => {
    setSelectedLabPanel(labPanel);
    setEditLabPanelModalOpen(true);
  };

  const handleSaveLabPanel = async (updatedLabPanel: any) => {
    try {
      const updatedLabPanels = logic.labPanels.map((lp: any) => {
        const lpId = lp.userLabPanelID || lp.id;
        const updatedId = updatedLabPanel.userLabPanelID || updatedLabPanel.id;
        return lpId === updatedId ? updatedLabPanel : lp;
      });

      const updatedLogicData = {
        ...logic,
        labPanels: updatedLabPanels,
      };

      await updateLogic(updatedLogicData);

      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: "Success",
        message: `Lab panel updated successfully!`,
      });
      ASSetTypeSnackBar("success");

      if (onUpdate) {
        await onUpdate();
      }

      setEditLabPanelModalOpen(false);
      setSelectedLabPanel(null);
    } catch (error) {
      console.error("Error updating lab panel:", error);

      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: "Error",
        message: "Failed to update lab panel. Please try again.",
      });
      ASSetTypeSnackBar("alert");
    }
  };

  const handleDeleteLabPanel = (labPanel: any) => {
    const panelId = labPanel.userLabPanelID || labPanel.id;
    const panelName = displayLabPanelName(labPanel.name, "Lab panel");

    // Show warning confirmation before deleting
    ASSetSnackBarMessage({
      title: "Delete lab panel",
      message:
        "Are you sure you want to delete this lab panel from the logic? This action cannot be undone.",
    });
    ASSetTypeSnackBar("warning");
    ASSetSnackBarActions({
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          // Remove the lab panel from the labPanels array (match on the id used
          // everywhere else — userLabPanelID, falling back to id).
          const updatedLabPanels = (logic.labPanels || []).filter(
            (lp: any) => (lp.userLabPanelID || lp.id) !== panelId
          );

          const updatedLogicData = {
            ...logic,
            labPanels: updatedLabPanels,
          };

          await updateLogic(updatedLogicData);

          ASSetOpenSnackBar(true);
          ASSetSnackBarMessage({
            title: "Success",
            message: `Lab panel "${panelName}" deleted successfully!`,
          });
          ASSetTypeSnackBar("success");

          if (onUpdate) {
            await onUpdate();
          }
        } catch (error) {
          console.error("Error deleting lab panel:", error);

          ASSetOpenSnackBar(true);
          ASSetSnackBarMessage({
            title: "Error",
            message: "Failed to delete lab panel. Please try again.",
          });
          ASSetTypeSnackBar("alert");
        } finally {
          setIsDeleting(false);
        }
      },
      onCancel: () => {
        // Just close the snackbar
      },
    });
    ASSetOpenSnackBar(true);
  };

  const createLabPanelMenuOptions = (labPanel: any): MenuOption[] => {
    const options: MenuOption[] = [
      {
        label: "Edit lab panel",
        onClick: () => handleEditLabPanel(labPanel),
      },
    ];

    // Offer delete as long as the logic keeps at least one outcome (a service
    // or another lab panel) — never let it be emptied to nothing.
    const totalOutcomes =
      (logic.services?.length || 0) + (logic.labPanels?.length || 0);
    if (totalOutcomes > 1) {
      options.push({
        label: "Delete lab panel",
        onClick: () => handleDeleteLabPanel(labPanel),
      });
    }

    return options;
  };

  // Create service-specific menu options
  const createServiceMenuOptions = (service: any): MenuOption[] => {
    const options: MenuOption[] = [
      {
        label: "Edit service logic",
        onClick: () => handleEditService(service),
      },
    ];

    // Only show delete option if there's more than one service
    if (logic.services.length > 1) {
      options.push({
        label: "Delete service logic",
        onClick: () => handleDeleteService(service),
      });
    }

    return options;
  };

  return (
    <div style={styles.container}>
      {/* No title or back button: the breadcrumb names the page and carries
          the link back to the listing. */}
      <div style={styles.contentWrapper}>
        {isTogglingStatus || isDeleting ? (
          <PageLoader />
        ) : (
          <ScrollableWrapper>
            <div style={styles.contentContainer(theme)}>
              {/* Logic Name and Actions */}
              <div style={styles.logicHeader}>
                <div style={styles.logicName(theme)}>{logic.name}</div>
                <div style={styles.logicActions}>
                  <IOSSwitch
                    checked={isEnabled}
                    onChange={handleToggleEnable}
                    leftLabel="Disabled"
                    rightLabel="Enabled"
                    showLabels={true}
                  />
                  {logic.isSystem === 0 && (
                    <MoreOptionsPopover
                      options={menuOptions}
                      icon={
                        <MoreIcon fill={theme.colors_ds.NEUTRAL_ICON_DEFAULT} />
                      }
                    />
                  )}
                </div>
              </div>
              {/* Rules Section */}
              {logic.rules &&
                logic.rules.list &&
                logic.rules.list.length > 0 && (
                  <RulesPreview rules={logic.rules} bgColor={"transparent"} />
                )}

              {/* Services & Details Section */}
              {logic.services && logic.services.length > 0 && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle(theme)}>
                    Services & Details
                  </div>
                  <div style={styles.servicesContainer(theme)}>
                    {logic.services.map((service: any, index: number) => (
                      <div key={service.id || index}>
                        {index > 0 && (
                          <div style={styles.serviceSeparator(theme)}></div>
                        )}
                        <div style={styles.serviceCard}>
                          {/* Service header */}
                          <div style={styles.serviceHeader}>
                            <div style={styles.serviceTitle(theme)}>
                              {service.name}
                            </div>

                            <MoreOptionsPopover
                              options={createServiceMenuOptions(service)}
                              icon={
                                <MoreIcon
                                  fill={theme.colors_ds.NEUTRAL_ICON_DEFAULT}
                                />
                              }
                              minWidth="160px"
                            />
                          </div>

                          <div style={styles.attributesGrid}>
                            <div style={styles.detailElement}>
                              <span style={styles.detailLabel(theme)}>
                                Service type
                              </span>
                              <span style={styles.detailValue(theme)}>
                                {enReportServiceType[service.serviceType]}
                              </span>
                            </div>
                            <div style={styles.detailElement}>
                              <span style={styles.detailLabel(theme)}>
                                Start offset
                              </span>
                              <span style={styles.detailValue(theme)}>
                                {`${service.startOffset} ${
                                  enTimeUnit[service.startOffsetUnit]
                                }`}
                              </span>
                            </div>
                            {/* Total Duration field */}
                            {service.serviceType !== 7 &&
                              service.serviceType !== 6 && (
                                <div style={styles.detailElement}>
                                  <span style={styles.detailLabel(theme)}>
                                    {service.serviceType === 3
                                      ? "Total Duration of Supplementation"
                                      : service.serviceType === 4
                                      ? "Total Duration of Administration"
                                      : "Total Treatment Duration"}
                                  </span>
                                  <span style={styles.detailValue(theme)}>
                                    {service.totalDurationUnit &&
                                    service.totalDurationUnit
                                      .toLowerCase()
                                      .trim() === "one-off"
                                      ? "one-off"
                                      : (() => {
                                          const hasSingleValue =
                                            service.totalDuration !== null &&
                                            service.totalDuration !==
                                              undefined &&
                                            service.totalDuration !== 0;
                                          const hasMinValue =
                                            service.totalDurationMin !== null &&
                                            service.totalDurationMin !==
                                              undefined &&
                                            service.totalDurationMin !== 0;
                                          const hasMaxValue =
                                            service.totalDurationMax !== null &&
                                            service.totalDurationMax !==
                                              undefined &&
                                            service.totalDurationMax !== 0;
                                          const hasRange =
                                            hasMinValue || hasMaxValue;

                                          // Prioritize range mode if any range values exist
                                          if (hasRange) {
                                            // Range mode
                                            const minVal =
                                              service.totalDurationMin ?? 0;
                                            const maxVal =
                                              service.totalDurationMax ?? 0;
                                            return `${minVal} - ${maxVal} ${
                                              service.totalDurationUnit || ""
                                            }`.trim();
                                          } else if (hasSingleValue) {
                                            // Single value mode
                                            return `${service.totalDuration} ${
                                              service.totalDurationUnit || ""
                                            }`.trim();
                                          }
                                          return "-";
                                        })()}
                                  </span>
                                </div>
                              )}

                            {/* Render service attributes */}
                            {service.attributes &&
                              service.attributes.length > 0 &&
                              service.attributes
                                .filter((a: any) => a.kind !== 1)
                                .sort((a: any, b: any) => {
                                  // Define priority order: "Sessions Frequency" first, then "IV Sessions Frequency", then others
                                  const getPriority = (name: string) => {
                                    if (name === "Sessions Frequency") return 0;
                                    if (name === "IV Sessions Frequency")
                                      return 1;
                                    if (name === "Follow-up Frequency")
                                      return 2;
                                    if (name === "Administration Frequency")
                                      return 3;
                                    return 4;
                                  };
                                  return (
                                    getPriority(a.name) - getPriority(b.name)
                                  );
                                })
                                .map((attribute: any, attrIndex: number) => {
                                  const displayValue =
                                    renderAttributeValue(attribute);

                                  if (
                                    !displayValue ||
                                    displayValue === "" ||
                                    displayValue === null ||
                                    displayValue === undefined
                                  ) {
                                    return (
                                      <div
                                        key={
                                          attribute.id ||
                                          attribute.attributeID ||
                                          attrIndex
                                        }
                                        style={styles.detailElement}
                                      >
                                        <span style={styles.detailLabel(theme)}>
                                          {attribute.name}
                                        </span>
                                        <span style={styles.detailValue(theme)}>
                                          -
                                        </span>
                                      </div>
                                    );
                                  }

                                  return (
                                    <div
                                      key={
                                        attribute.id ||
                                        attribute.attributeID ||
                                        attrIndex
                                      }
                                      style={styles.detailElement}
                                    >
                                      <span style={styles.detailLabel(theme)}>
                                        {attribute.name}
                                      </span>
                                      <span style={styles.detailValue(theme)}>
                                        {displayValue}
                                      </span>
                                    </div>
                                  );
                                })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Lab Panels Section */}
              {logic.labPanels && logic.labPanels.length > 0 && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle(theme)}>Lab Panels</div>
                  <div style={styles.servicesContainer(theme)}>
                    {logic.labPanels.map((labPanel: any, index: number) => {
                      const panelId = labPanel.userLabPanelID || labPanel.id;
                      const panelName = displayLabPanelName(labPanel.name);

                      return (
                        <div key={panelId || index}>
                          {index > 0 && (
                            <div style={styles.serviceSeparator(theme)}></div>
                          )}
                          <div style={styles.serviceCard}>
                            <div style={styles.serviceHeader}>
                              <div style={styles.serviceTitle(theme)}>
                                {panelName}
                              </div>
                              <MoreOptionsPopover
                                options={createLabPanelMenuOptions(labPanel)}
                                icon={
                                  <MoreIcon
                                    fill={theme.colors_ds.NEUTRAL_ICON_DEFAULT}
                                  />
                                }
                                minWidth="160px"
                              />
                            </div>

                            <div style={styles.attributesGrid}>
                              <div style={styles.detailElement}>
                                <span style={styles.detailLabel(theme)}>
                                  Service type
                                </span>
                                <span style={styles.detailValue(theme)}>
                                  Lab panel
                                </span>
                              </div>
                              <div style={styles.detailElement}>
                                <span style={styles.detailLabel(theme)}>
                                  Start offset
                                </span>
                                <span style={styles.detailValue(theme)}>
                                  {labPanel.startOffset != null &&
                                  labPanel.startOffsetUnit != null
                                    ? `${labPanel.startOffset} ${
                                        enTimeUnit[labPanel.startOffsetUnit] ||
                                        ""
                                      }`
                                    : "-"}
                                </span>
                              </div>
                            </div>

                            {/* Panel summary (provider, test type, cost, biomarkers) */}
                            <div style={{ marginTop: "16px" }}>
                              <PanelSummaryCard panel={labPanel} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No services or lab panels message */}
              {(!logic.services || logic.services.length === 0) &&
                (!logic.labPanels || logic.labPanels.length === 0) && (
                  <div style={styles.section}>
                    <div style={styles.sectionTitle(theme)}>
                      Services & Details
                    </div>
                    <div style={styles.servicesContainer(theme)}>
                      <div style={styles.noServices(theme)}>
                        No services or lab panels configured for this logic
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </ScrollableWrapper>
        )}
      </div>

      {/* Edit Service Modal */}
      <EditServiceModal
        open={editServiceModalOpen}
        service={selectedService}
        onClose={() => {
          setEditServiceModalOpen(false);
          setSelectedService(null);
        }}
        onSave={handleSaveService}
        type="logicBuilder"
      />

      {/* Edit Lab Panel Modal */}
      <EditLabPanelModal
        open={editLabPanelModalOpen}
        labPanel={selectedLabPanel}
        onClose={() => {
          setEditLabPanelModalOpen(false);
          setSelectedLabPanel(null);
        }}
        onSave={handleSaveLabPanel}
        type="logicBuilder"
      />

      {/* Edit Logic Modal */}
      <NewLogicModal
        openModal={editLogicModalOpen}
        handleClose={() => setEditLogicModalOpen(false)}
        onLogicSaved={handleLogicSaved}
        editingLogic={logic}
        isEditMode={true}
      />
    </div>
  );
};

export default LogicDetailsView;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    height: "100%",
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: "1", // Take remaining space
    minHeight: "0", // Important for ScrollableWrapper to work
    overflow: "hidden" as "hidden", // Let ScrollableWrapper handle overflow
  },
  contentContainer: (theme: Theme) => ({
    padding: "16px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
  }),

  logicHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  logicName: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "20px",
    lineHeight: "28px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  logicActions: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  section: {
    marginTop: "8px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  sectionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "16px",
    lineHeight: "22px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  servicesContainer: (theme: Theme) => ({
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "16px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
  }),
  serviceCard: {
    display: "flex",
    flexDirection: "column" as "column",
  },
  serviceHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  serviceTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "16px",
    lineHeight: "22px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  attributesGrid: {
    paddingTop: "16px",
    display: "grid",
    gridTemplateColumns: "300px 300px",
    gap: "16px 52px",
  },
  detailElement: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    alignItems: "flex-start",
    width: "100%",
  },
  detailLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  detailValue: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  serviceSeparator: (theme: Theme) => ({
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    marginBottom: "16px",
  }),
  noServices: (theme: Theme) => ({
    padding: "32px",
    textAlign: "center" as "center",
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
